// The site filters shared by the Bulk Bill Run board and the Bulk Customer
// Report: the same five fields the Bills list filters on, read off the bill's
// joined site so a list of bills can be narrowed without another query.
import type { BvBill } from '../types/billTypes';

export const SITE_FILTERS = [
  { key: 'entity', label: 'Entity' },
  { key: 'retailer', label: 'Retailer' },
  { key: 'state', label: 'State' },
  { key: 'distributor', label: 'Distributor' },
  { key: 'tariff', label: 'Tariff' },
] as const;

export type SiteFilterKey = (typeof SITE_FILTERS)[number]['key'];
export type SiteFilterValues = Record<SiteFilterKey, string>;

/** No filter set: '' means "all" for every field. */
export const NO_SITE_FILTERS: SiteFilterValues = { entity: '', retailer: '', state: '', distributor: '', tariff: '' };

/** The bill's value for one filter field, trimmed; '' when the site has none. */
export function siteValue(bill: Pick<BvBill, 'site'>, key: SiteFilterKey): string {
  const site = bill.site;
  const extn = (site?.bv_extn ?? {}) as Record<string, unknown>;
  const v = key === 'entity' ? site?.client?.name
    : key === 'retailer' ? site?.current_retailer
    : key === 'state' ? site?.addr_state
    : key === 'distributor' ? extn.network_distributor
    : extn.network_tariff_code;
  return typeof v === 'string' ? v.trim() : '';
}

/** The values each dropdown offers: every distinct non-empty value, sorted. */
export function siteFilterOptions(bills: Pick<BvBill, 'site'>[]): Record<SiteFilterKey, string[]> {
  return Object.fromEntries(SITE_FILTERS.map(({ key }) => [
    key, [...new Set(bills.map((b) => siteValue(b, key)).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
  ])) as Record<SiteFilterKey, string[]>;
}

/** True when the bill matches every filter that is set. */
export function matchesSiteFilters(bill: Pick<BvBill, 'site'>, filters: SiteFilterValues): boolean {
  return SITE_FILTERS.every(({ key }) => !filters[key] || siteValue(bill, key) === filters[key]);
}
